import React, { useMemo, useState } from 'react';
import GenericDropdown from './GenericDropdown';
import ApiQueryParamsEditor from './ApiQueryParamsEditor';
import './ApiSourceRequestTabs.css';

const AUTH_TYPE_OPTIONS = [
  { id: 'none', name: 'No Authentication' },
  { id: 'bearer', name: 'Bearer Token' },
  { id: 'basic', name: 'Basic Auth' },
  { id: 'api_key', name: 'API Key (Header)' },
];

const TAB_DEFS = [
  { id: 'params', label: 'Params' },
  { id: 'headers', label: 'Headers' },
  { id: 'auth', label: 'Auth' },
  { id: 'body', label: 'Body' },
];

export default function ApiSourceRequestTabs({
  httpMethod = 'GET',
  endpoint = '',
  previewApiSource,
  queryParamRows,
  onQueryParamRowsChange,
  headerParamRows,
  onHeaderParamRowsChange,
  bodyParamRows,
  onBodyParamRowsChange,
  authType,
  onAuthTypeChange,
  authToken,
  onAuthTokenChange,
  authReadOnly = false,
  showBodyTab = true,
  previewOptions = null,
}) {
  const [activeTab, setActiveTab] = useState(httpMethod === 'POST' ? 'body' : 'params');

  const visibleTabs = useMemo(() => {
    return TAB_DEFS.filter((tab) => {
      if (tab.id === 'body') return showBodyTab && httpMethod === 'POST';
      return true;
    });
  }, [httpMethod, showBodyTab]);

  const effectiveTab = visibleTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : (visibleTabs[0]?.id || 'params');

  const harnessPreviewSource = previewApiSource;

  return (
    <div className="api-source-request-tabs">
      <div className="api-source-request-tab-bar" role="tablist" aria-label="API request configuration">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={effectiveTab === tab.id}
            className={`api-source-request-tab${effectiveTab === tab.id ? ' is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="api-source-request-tab-panel">
        {effectiveTab === 'params' && (
          <ApiQueryParamsEditor
            rows={queryParamRows}
            onChange={onQueryParamRowsChange}
            apiSource={harnessPreviewSource}
            previewMode="query"
            previewOptions={previewOptions}
            hint={
              httpMethod === 'POST'
                ? 'URL query parameters (optional). Leave blank to send none.'
                : 'Query string parameters. Leave rows empty to send none.'
            }
          />
        )}

        {effectiveTab === 'headers' && (
          <ApiQueryParamsEditor
            rows={headerParamRows}
            onChange={onHeaderParamRowsChange}
            apiSource={{
              ...harnessPreviewSource,
              header_param_defs: headerParamRows?.length
                ? headerParamRows.map((row) => ({
                  key: row.key,
                  default: row.value,
                  label: row.label || row.key,
                })).filter((row) => row.key)
                : [],
            }}
            previewMode="headers"
            previewOptions={previewOptions}
            keyPlaceholder="Content-Type"
            valuePlaceholder="application/json"
            hint="Custom request headers. POST adds Content-Type: application/json automatically unless you set it here."
          />
        )}

        {effectiveTab === 'auth' && (
          <div className="api-source-auth-panel">
            {authReadOnly ? (
              <div className="hint">
                Auth type: <strong>{authType || 'none'}</strong>.
                {' '}Configure credentials in <strong>Settings → Ingestion → Auth</strong> tab.
                The harness uses saved group credentials and shows whether they were applied in the request debug output.
              </div>
            ) : (
              <>
                <div className="form-row">
                  <label>Authentication</label>
                  <GenericDropdown
                    items={AUTH_TYPE_OPTIONS}
                    value={authType}
                    onChange={(id) => onAuthTypeChange?.(id || 'none')}
                    placeholder="Select auth type"
                  />
                </div>
                {authType !== 'none' && (
                  <div className="form-row">
                    <label>
                      {authType === 'bearer' && 'Bearer Token'}
                      {authType === 'basic' && 'Credentials (user:password or token:api_token)'}
                      {authType === 'api_key' && 'API Key'}
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={authToken}
                      onChange={(e) => onAuthTokenChange?.(e.target.value)}
                      placeholder={
                        authType === 'bearer' ? 'Bearer token value'
                          : authType === 'basic' ? 'username:password'
                            : 'API key value'
                      }
                    />
                    <div className="hint">
                      {authType === 'bearer' && 'Sent as Authorization: Bearer …'}
                      {authType === 'basic' && 'Sent as Authorization: Basic … (Toggl: api_token:api_token)'}
                      {authType === 'api_key' && 'Sent in X-Api-Key by default'}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {effectiveTab === 'body' && httpMethod === 'POST' && (
          <ApiQueryParamsEditor
            rows={bodyParamRows}
            onChange={onBodyParamRowsChange}
            apiSource={harnessPreviewSource}
            previewMode="body"
            previewOptions={previewOptions}
            keyPlaceholder="from"
            valuePlaceholder="7 days ago"
            hint="JSON body fields. Dates: now, N days ago, YYYY-MM-DD. Numbers are sent as JSON numbers."
          />
        )}
      </div>
    </div>
  );
}
